/**
 * libraryStore — Gerencia referências bibliográficas (works) e fichamentos do usuário
 *
 * Consumidores: Biblioteca, Leituras, Conexoes, Inspector, PdfReaderModal, FichamentoPanel, +15 componentes
 * Dependências: googleDrive, preferencesStore
 * Persistência: Google Drive (referencias.json por projeto)
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Work, Fichamento, type MyAuthorshipStatus } from '../types/work';
import { nanoid } from 'nanoid';
import { googleDrive } from '../services/googleDrive';
import { generateFilenameWithPreferences } from '../utils/libraryFilenameGenerator';
import { usePreferencesStore } from './preferencesStore';
import { logger } from '../utils/logger';
import { worksCache, getLibraryCacheKey } from './libraryCache';
import { detectMyPublications } from '../utils/authorshipDetection';
import { buildMinhaObra } from '../services/minhasObras/authorDetection';
import { addObraPendente } from '../services/minhasObras/minhasObrasService';
import { detectMetadataStatus } from '../services/library/metadataVerificationService';
import { bigwaToast } from '../utils/bigwaToast';
import { driveErrorToast } from '../utils/driveErrorToast';
import { DriveConflictError } from '../services/googleDrive/driveFetch';
import { saveReferencesQueued, saveReferencesDebounced } from '../services/googleDrive/referencesWriteQueue';
import { auditLog } from '../utils/auditLog';
import { trackPWAEngagement } from '../hooks/usePWAEngagement';

/** Timestamp da última mutação local persistida ao Drive — cross-device sync guard */
let _lastMutationAt = 0;

interface LibraryState {
  works: Work[];
  isLoading: boolean;
  error: string | null;
  worksLoaded: boolean;
  worksLoadedAt: number | null;
  
  loadWorks: () => Promise<void>;
  forceReload: () => Promise<void>;  // 🆕 Nova função para reload forçado
  revalidateIfStale: () => void;
  addWork: (work: Work) => Promise<Work>;
  updateWork: (id: string, data: Partial<Work>) => Promise<void>;
  deleteWork: (id: string) => Promise<void>;
  restoreWork: (id: string) => Promise<void>;
  deletePermanently: (id: string) => Promise<void>;
  deletePermanentlyBulk: (ids: string[]) => Promise<void>;
  clearAllWorks: () => Promise<void>;
  searchWorks: (query: string) => Work[];
  getActiveWorks: () => Work[];
  getDeletedWorks: () => Work[];
  
  // Fichamento methods
  createFichamento: (workId: string) => Fichamento;
  updateFichamento: (workId: string, updates: Partial<Fichamento>) => void;
  toggleFichamentoStatus: (workId: string) => void;
  
  // PDF orphan scan
  scanOrphanPdfs: () => Promise<Array<{ id: string; name: string; createdTime: string; size?: string }>>;
}

export const useLibraryStore = create<LibraryState>()(
  devtools(
    (set, get) => ({
        works: [],
        isLoading: false,
        error: null,
        worksLoaded: false,
        worksLoadedAt: null as number | null,

        loadWorks: async () => {
          const loadedAt = get().worksLoadedAt;
          const { worksLoaded, isLoading: alreadyLoading, works: currentWorks } = get();

          // Guard 1: TTL fresh — skip completely
          if (loadedAt && Date.now() - loadedAt < 300000) return;

          // Guard 2: já está carregando — não duplicar
          if (alreadyLoading) return;

          // Guard 3: dados em memória + TTL expirado → revalidar silenciosamente (sem skeleton)
          if (worksLoaded && currentWorks.length > 0) {
            // Cross-device sync guard: skip revalidation if this tab mutated recently
            if (_lastMutationAt && Date.now() - _lastMutationAt < 300000) return;
            // Revalidar em background — dados existentes continuam renderizados
            set({ worksLoadedAt: Date.now() }); // prevenir re-entry
            const cacheKey = getLibraryCacheKey();
            googleDrive.loadReferencesFromDrive().then(freshWorks => {
              if (freshWorks !== null) {
                set({ works: freshWorks });
                worksCache.set(cacheKey, freshWorks);
              }
            }).catch((err) => {
              logger.error('[libraryStore.loadWorks] Revalidação silenciosa falhou:', String(err));
            });
            return;
          }

          const cacheKey = getLibraryCacheKey();

          // Tentar IndexedDB (instantâneo — stale-while-revalidate)
          try {
            const cached = await worksCache.get(cacheKey);
            if (cached.success && cached.data !== null) {
              // Serve stale imediatamente — SEM isLoading (sem skeleton)
              set({ works: cached.data, worksLoaded: true, worksLoadedAt: Date.now(), error: null });

              // Cross-device sync guard: skip revalidation if this tab mutated recently
              if (_lastMutationAt && Date.now() - _lastMutationAt < 300000) return;

              // Revalidar em background (silencioso)
              googleDrive.loadReferencesFromDrive().then(freshWorks => {
                if (freshWorks !== null) {
                  set({ works: freshWorks });
                  worksCache.set(cacheKey, freshWorks);
                }
              }).catch((err) => {
                logger.error('[libraryStore.loadWorks] Revalidação falhou:', String(err));
                driveErrorToast(err, 'Erro ao carregar referências atualizadas. Usando dados em cache.');
              });

              return;
            }
          } catch {
            // IndexedDB indisponível — fallback para Drive
          }

          // Sem cache nenhum: buscar do Drive (skeleton aceito neste caso — primeiro acesso)
          set({ isLoading: true, error: null });
          try {
            const works = await googleDrive.loadReferencesFromDrive();
            const data = works ?? [];
            set({ works: data, isLoading: false, worksLoaded: true, worksLoadedAt: Date.now() });

            // Salvar no IndexedDB para próxima abertura
            worksCache.set(cacheKey, data);
          } catch (error: unknown) {
            logger.error('LibraryStore', 'Failed to load works:', error);
            set({ error: error instanceof Error ? error.message : String(error), isLoading: false });
          }
        },

        // Força reload do Drive (invalida cache, mas NÃO limpa works — atomic swap)
        forceReload: async () => {
          // Invalidar cache
          const cacheKey = getLibraryCacheKey();
          worksCache.delete(cacheKey);

          // NÃO setar worksLoaded=false nem limpar works[] — dados existentes
          // continuam renderizados enquanto o Drive responde (zero skeleton)
          set({ error: null, worksLoadedAt: null });
          try {
            const works = await googleDrive.loadReferencesFromDrive();
            const data = works ?? [];
            // Atomic swap: dados novos substituem antigos em um único set()
            set({ works: data, isLoading: false, worksLoaded: true, worksLoadedAt: Date.now() });
            worksCache.set(cacheKey, data);
          } catch (error: unknown) {
            logger.error('LibraryStore', 'Failed to force reload:', error);
            set({ error: error instanceof Error ? error.message : String(error), isLoading: false });
          }
        },
        
        // Cross-device sync: revalidar dados do Drive se TTL expirou
        revalidateIfStale: () => {
          const loadedAt = get().worksLoadedAt;
          // Skip if fresh (< 5 min)
          if (loadedAt && Date.now() - loadedAt < 300000) return;
          // Skip if this tab mutated recently — local state is authoritative
          if (_lastMutationAt && Date.now() - _lastMutationAt < 300000) return;
          // Skip if already loading
          if (get().isLoading) return;

          // Background revalidation — no skeleton, no isLoading
          set({ worksLoadedAt: Date.now() }); // prevent re-entry
          const cacheKey = getLibraryCacheKey();
          googleDrive.loadReferencesFromDrive().then(freshWorks => {
            if (freshWorks !== null) {
              set({ works: freshWorks });
              worksCache.set(cacheKey, freshWorks);
            }
          }).catch(err => {
            logger.error('[libraryStore.revalidateIfStale] Revalidação silenciosa falhou:', String(err));
          });
        },

        addWork: async (work) => {
          set({ isLoading: true, error: null });

          // Snapshot do estado anterior para rollback
          const previousWorks = get().works;

          try {
            // Dedup 1: driveFileId
            if (work.driveFileId) {
              const existing = get().works.find(
                w => w.driveFileId === work.driveFileId && !w.isDeleted
              );
              if (existing) {
                logger.warn('LibraryStore', `Duplicata por driveFileId: ${existing.id}`);
                set({ isLoading: false });
                bigwaToast.info('Esta referência já está na sua biblioteca.');
                return existing;
              }
            }

            // Dedup 2: contentHash (SHA-256)
            if ((work as Work).contentHash) {
              const existing = get().works.find(
                w => (w as Work).contentHash === (work as Work).contentHash && !w.isDeleted
              );
              if (existing) {
                logger.warn('LibraryStore', `Duplicata por contentHash: ${existing.id}`);
                set({ isLoading: false });
                bigwaToast.info('Esta referência já está na sua biblioteca.');
                return existing;
              }
            }

            // Detecção de autoria
            let shouldRegisterObra = false;
            const prefs = usePreferencesStore.getState().preferences;
            if (prefs.academicName?.trim()) {
              const { newDetections } = detectMyPublications(
                [work as Work],
                prefs.academicName,
                prefs.alternativeNames || [],
                prefs.detectOrientations || false
              );
              if (newDetections.length > 0) {
                (work as Work).myAuthorship = 'auto-detected' as MyAuthorshipStatus;
                shouldRegisterObra = true;
              }
            }

            // Detectar status de metadados
            (work as Work).metadataStatus = detectMetadataStatus(work as Work);

            // Salva no Drive ANTES de atualizar o estado local
            const newWorks = [...(previousWorks || []), work];
            await saveReferencesQueued(newWorks);
            worksCache.set(getLibraryCacheKey(), newWorks);

            // Drive confirmou — agora atualiza o estado local
            set({ works: newWorks, isLoading: false });
            _lastMutationAt = Date.now();

            // Registrar obra pendente
            if (shouldRegisterObra) {
              const minhaObra = buildMinhaObra(work as Work, 'capture');
              addObraPendente(minhaObra).catch(err =>
                logger.error('LibraryStore', 'Erro ao registrar obra pendente:', err)
              );
              window.dispatchEvent(new CustomEvent('bigwa:authorship-detected'));
            }

            auditLog({ operation: 'create', target: 'referência', targetId: work.id, detail: `Referência criada: ${work.title}`, success: true });
            trackPWAEngagement(1);
            return work;
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) {
              bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 });
              get().forceReload();
              throw error;
            }
            // Rollback — restaura estado anterior
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            driveErrorToast(error, 'Não foi possível salvar a referência. Tente novamente.');
            auditLog({ operation: 'create', target: 'referência', targetId: work.id, detail: `Falha ao criar referência: ${work.title}`, success: false, error: String(error) });
            throw error;
          }
        },

        updateWork: async (id, data) => {
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();

            // ═══ RENAME RETROATIVO: detectar se metadados relevantes mudaram ═══
            const existingWork = get().works.find(w => w.id === id);
            let renameData: Partial<Work> = {};

            if (existingWork?.driveFileId) {
              const metadataChanged =
                (data.authors !== undefined && JSON.stringify(data.authors) !== JSON.stringify(existingWork.authors)) ||
                (data.title !== undefined && data.title !== existingWork.title) ||
                (data.year !== undefined && data.year !== existingWork.year);

              if (metadataChanged) {
                const mergedWork = { ...existingWork, ...data };
                const hasAuthors = mergedWork.authors && mergedWork.authors.length > 0 &&
                  (mergedWork.authors[0].lastName || mergedWork.authors[0].firstName);

                if (hasAuthors) {
                  try {
                    const namingPrefs = usePreferencesStore.getState().preferences.namingPreferences;
                    const existingNames = get().works
                      .filter(w => w.id !== id && w.driveFileName)
                      .map(w => w.driveFileName!);
                    const result = generateFilenameWithPreferences(mergedWork, existingNames, namingPrefs);
                    const currentName = existingWork.driveFileName || '';
                    const newName = result.filename;
                    if (newName !== currentName) {
                      const renamed = await googleDrive.renameFile(existingWork.driveFileId, newName);
                      if (renamed) {
                        renameData = { driveFileName: newName };
                      }
                    }
                  } catch (renameError) {
                    logger.warn('LibraryStore', 'Rename retroativo falhou (não-crítico):', renameError);
                  }
                }
              }
            }
            // ═══ FIM RENAME RETROATIVO ═══

            set((state) => ({
              works: state.works.map((w) =>
                w.id === id ? { ...w, ...data, ...renameData, updatedAt: now } as Work : w
              ),
              isLoading: false,
            }));
            _lastMutationAt = Date.now();

            worksCache.set(getLibraryCacheKey(), get().works);
            saveReferencesDebounced(() => get().works, previousWorks);
            const updatedWork = get().works.find(w => w.id === id);
            auditLog({ operation: 'update', target: 'referência', targetId: id, detail: `Referência atualizada: ${updatedWork?.title ?? id}`, success: true });
          } catch (error: unknown) {
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.updateWork]', String(error));
            driveErrorToast(error, 'Não foi possível salvar a edição. Suas alterações foram desfeitas.');
            const failedWork = previousWorks.find(w => w.id === id);
            auditLog({ operation: 'update', target: 'referência', targetId: id, detail: `Falha ao atualizar referência: ${failedWork?.title ?? id}`, success: false, error: String(error) });
          }
        },

        deleteWork: async (id) => {
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();
            set((state) => ({
              works: state.works.map((w) =>
                w.id === id ? { ...w, isDeleted: true, deletedAt: now, updatedAt: now } as Work : w
              ),
              isLoading: false,
            }));
            _lastMutationAt = Date.now();
            const currentWorks = get().works;
            await saveReferencesQueued(currentWorks);
            worksCache.set(getLibraryCacheKey(), currentWorks);
            const deletedWork = currentWorks.find(w => w.id === id);
            auditLog({ operation: 'delete', target: 'referência', targetId: id, detail: `Movido para lixeira: ${deletedWork?.title ?? id}`, success: true });
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) { bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 }); get().forceReload(); return; }
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.deleteWork]', String(error));
            driveErrorToast(error, 'Não foi possível mover para a lixeira. Alteração desfeita.');
            const failedWork = previousWorks.find(w => w.id === id);
            auditLog({ operation: 'delete', target: 'referência', targetId: id, detail: `Falha ao mover para lixeira: ${failedWork?.title ?? id}`, success: false, error: String(error) });
          }
        },

        restoreWork: async (id) => {
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();
            set((state) => ({
              works: state.works.map((w) =>
                w.id === id ? { ...w, isDeleted: false, deletedAt: undefined, updatedAt: now } as Work : w
              ),
              isLoading: false,
            }));
            _lastMutationAt = Date.now();
            const currentWorks = get().works;
            await saveReferencesQueued(currentWorks);
            worksCache.set(getLibraryCacheKey(), currentWorks);
            const restoredWork = currentWorks.find(w => w.id === id);
            auditLog({ operation: 'update', target: 'referência', targetId: id, detail: `Restaurado da lixeira: ${restoredWork?.title ?? id}`, success: true });
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) { bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 }); get().forceReload(); return; }
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.restoreWork]', String(error));
            driveErrorToast(error, 'Não foi possível restaurar a referência. Alteração desfeita.');
            const failedWork = previousWorks.find(w => w.id === id);
            auditLog({ operation: 'update', target: 'referência', targetId: id, detail: `Falha ao restaurar da lixeira: ${failedWork?.title ?? id}`, success: false, error: String(error) });
          }
        },

        deletePermanently: async (id) => {
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          try {
            const deletedWork = previousWorks.find(w => w.id === id);
            set((state) => ({
              works: state.works.filter((w) => w.id !== id),
              isLoading: false,
            }));
            _lastMutationAt = Date.now();
            const currentWorks = get().works;
            await saveReferencesQueued(currentWorks);
            worksCache.set(getLibraryCacheKey(), currentWorks);

            // Best-effort cleanup: PDF, highlights, fichamento attachments
            // Failures do NOT revert the reference deletion
            const hadPdf = !!deletedWork?.driveFileId;

            // 1. Delete PDF from Drive
            if (deletedWork?.driveFileId) {
              try {
                await googleDrive.deleteFichamentoFile(deletedWork.driveFileId);
              } catch (e) {
                logger.error('[libraryStore.deletePermanently] Failed to delete PDF:', String(e));
              }
            }

            // 2. Delete highlights from Drive
            try {
              await googleDrive.deleteHighlights(id);
            } catch (e) {
              logger.error('[libraryStore.deletePermanently] Failed to delete highlights:', String(e));
            }

            // 3. Delete highlights from IndexedDB
            try {
              const { indexedDBService } = await import('../services/indexedDBService');
              await indexedDBService.deleteHighlights(id);
            } catch (e) {
              logger.error('[libraryStore.deletePermanently] Failed to delete IndexedDB highlights:', String(e));
            }

            // 4. Delete fichamento attachments from Drive
            if (deletedWork?.fichamento?.attachments?.length) {
              for (const att of deletedWork.fichamento.attachments) {
                try {
                  await googleDrive.deleteFichamentoFile(att.id);
                } catch (e) {
                  logger.error('[libraryStore.deletePermanently] Failed to delete fichamento attachment:', String(e));
                }
              }
            }

            // 5. Remove reference ID from all project.json files (prevents orphan IDs)
            try {
              const { useDriveProjectStore } = await import('./driveProjectStore');
              const projects = useDriveProjectStore.getState().projects || [];
              for (const project of projects) {
                try {
                  await googleDrive.removeReferenceFromProject(project.id, id);
                } catch {
                  // Best-effort — project may not contain this reference
                }
              }
            } catch (e) {
              logger.error('[libraryStore.deletePermanently] Failed to cleanup project references:', String(e));
            }

            auditLog({ operation: 'delete', target: 'referência', targetId: id, detail: `Deletado permanentemente${hadPdf ? ' (PDF + highlights limpos)' : ''}: ${deletedWork?.title ?? id}`, success: true });
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) { bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 }); get().forceReload(); return; }
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.deletePermanently]', String(error));
            driveErrorToast(error, 'Não foi possível excluir permanentemente. Alteração desfeita.');
            const failedWork = previousWorks.find(w => w.id === id);
            auditLog({ operation: 'delete', target: 'referência', targetId: id, detail: `Falha ao deletar permanentemente: ${failedWork?.title ?? id}`, success: false, error: String(error) });
          }
        },

        deletePermanentlyBulk: async (ids) => {
          if (ids.length === 0) return;
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          const idsSet = new Set(ids);
          const deletedWorks = previousWorks.filter(w => idsSet.has(w.id));
          try {
            // 1. Remove all at once from state + single save
            set((state) => ({
              works: state.works.filter((w) => !idsSet.has(w.id)),
              isLoading: false,
            }));
            _lastMutationAt = Date.now();
            const currentWorks = get().works;
            await saveReferencesQueued(currentWorks);
            worksCache.set(getLibraryCacheKey(), currentWorks);

            // 2. Best-effort cleanup: parallelize all cleanup per work
            const { indexedDBService } = await import('../services/indexedDBService');
            const { useDriveProjectStore } = await import('./driveProjectStore');
            const projects = useDriveProjectStore.getState().projects || [];

            const cleanupPromises = deletedWorks.map(async (work) => {
              const tasks: Promise<void>[] = [];

              // Delete PDF
              if (work.driveFileId) {
                tasks.push(
                  googleDrive.deleteFichamentoFile(work.driveFileId).catch(e =>
                    logger.error('[libraryStore.deletePermanentlyBulk] Failed to delete PDF:', String(e))
                  )
                );
              }

              // Delete highlights from Drive
              tasks.push(
                googleDrive.deleteHighlights(work.id).catch(e =>
                  logger.error('[libraryStore.deletePermanentlyBulk] Failed to delete highlights:', String(e))
                )
              );

              // Delete highlights from IndexedDB
              tasks.push(
                indexedDBService.deleteHighlights(work.id).catch(e =>
                  logger.error('[libraryStore.deletePermanentlyBulk] Failed to delete IndexedDB highlights:', String(e))
                )
              );

              // Delete fichamento attachments
              if (work.fichamento?.attachments?.length) {
                for (const att of work.fichamento.attachments) {
                  tasks.push(
                    googleDrive.deleteFichamentoFile(att.id).catch(e =>
                      logger.error('[libraryStore.deletePermanentlyBulk] Failed to delete attachment:', String(e))
                    )
                  );
                }
              }

              // Remove reference from projects
              for (const project of projects) {
                tasks.push(
                  googleDrive.removeReferenceFromProject(project.id, work.id).catch(() => {
                    // Best-effort — project may not contain this reference
                  })
                );
              }

              await Promise.allSettled(tasks);
            });

            await Promise.allSettled(cleanupPromises);

            for (const work of deletedWorks) {
              auditLog({ operation: 'delete', target: 'referência', targetId: work.id, detail: `Deletado permanentemente (bulk): ${work.title ?? work.id}`, success: true });
            }
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) { bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 }); get().forceReload(); return; }
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.deletePermanentlyBulk]', String(error));
            driveErrorToast(error, 'Não foi possível esvaziar a lixeira. Alterações desfeitas.');
            for (const work of deletedWorks) {
              auditLog({ operation: 'delete', target: 'referência', targetId: work.id, detail: `Falha ao deletar permanentemente (bulk): ${work.title ?? work.id}`, success: false, error: String(error) });
            }
          }
        },

        clearAllWorks: async () => {
          set({ isLoading: true, error: null });
          const previousWorks = get().works;
          try {
            set({ works: [], isLoading: false });
            _lastMutationAt = Date.now();
            await saveReferencesQueued([]);
            worksCache.set(getLibraryCacheKey(), []);
            auditLog({ operation: 'delete', target: 'referências', detail: `Todas as referências removidas (${previousWorks.length} itens)`, success: true });

            // Best-effort cleanup: delete associated Drive files (PDFs, highlights, fichamentos)
            // Runs after references are already cleared — failures do NOT revert
            // Parallelized in batches of 5 with Promise.allSettled (#22)
            if (previousWorks.length > 0) {
              const cleanupToastId = bigwaToast.loading('Limpando arquivos associados...');
              let failCount = 0;
              try {
                const BATCH_SIZE = 5;
                for (let i = 0; i < previousWorks.length; i += BATCH_SIZE) {
                  const batch = previousWorks.slice(i, i + BATCH_SIZE);
                  const results = await Promise.allSettled(batch.map(async (work) => {
                    // 1. Delete PDF from Drive
                    if (work.driveFileId) {
                      await googleDrive.deleteFichamentoFile(work.driveFileId).catch((e) => {
                        logger.error('[libraryStore.clearAllWorks] Failed to delete PDF:', String(e));
                      });
                    }
                    // 2. Delete highlights from Drive
                    await googleDrive.deleteHighlights(work.id).catch((e) => {
                      logger.error('[libraryStore.clearAllWorks] Failed to delete highlights:', String(e));
                    });
                    // 3. Delete highlights from IndexedDB
                    const { indexedDBService } = await import('../services/indexedDBService');
                    await indexedDBService.deleteHighlights(work.id).catch((e) => {
                      logger.error('[libraryStore.clearAllWorks] Failed to delete IndexedDB highlights:', String(e));
                    });
                    // 4. Delete fichamento attachments from Drive
                    if (work.fichamento?.attachments?.length) {
                      for (const att of work.fichamento.attachments) {
                        await googleDrive.deleteFichamentoFile(att.id).catch((e) => {
                          logger.error('[libraryStore.clearAllWorks] Failed to delete fichamento attachment:', String(e));
                        });
                      }
                    }
                  }));
                  failCount += results.filter(r => r.status === 'rejected').length;
                }
                if (failCount > 0) {
                  bigwaToast.notify(`Biblioteca limpa (${failCount} arquivo(s) não puderam ser removidos do Drive)`, { id: cleanupToastId });
                } else {
                  bigwaToast.notify('Biblioteca limpa com sucesso', { id: cleanupToastId });
                }
              } catch (cleanupError) {
                logger.error('[libraryStore.clearAllWorks] Cleanup failed:', String(cleanupError));
                bigwaToast.dismiss(cleanupToastId);
                bigwaToast.notify('Biblioteca limpa (alguns arquivos podem não ter sido removidos do Drive)');
              }
            }
          } catch (error: unknown) {
            if (error instanceof DriveConflictError) { bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 }); get().forceReload(); return; }
            set({ works: previousWorks, error: error instanceof Error ? error.message : String(error), isLoading: false });
            logger.error('[libraryStore.clearAllWorks]', String(error));
            driveErrorToast(error, 'Não foi possível limpar a biblioteca. Alteração desfeita.');
            auditLog({ operation: 'delete', target: 'referências', detail: `Falha ao remover todas as referências (${previousWorks.length} itens)`, success: false, error: String(error) });
          }
        },

        searchWorks: (query) => {
          const state = get();
          const lowerQuery = query.toLowerCase();
          return state.works.filter(
            (w) =>
              !w.isDeleted &&
              (w.title.toLowerCase().includes(lowerQuery) ||
              w.authors.some((a) => {
                const authorStr = `${a.lastName} ${a.firstName}`.toLowerCase();
                return authorStr.includes(lowerQuery);
              }) ||
              (w.tags && w.tags.some((t) => t.toLowerCase().includes(lowerQuery))))
          );
        },
        
        getActiveWorks: () => {
          const state = get();
          return (state.works || []).filter((w) => !w.isDeleted);
        },
        
        getDeletedWorks: () => {
          const state = get();
          return (state.works || []).filter((w) => w.isDeleted === true);
        },
        
        // Create fichamento for a work
        createFichamento: (workId) => {
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();
            const fichamento: Fichamento = {
              id: nanoid(),
              workId,
              content: '',
              status: 'draft',
              wordCount: 0,
              createdAt: now,
              updatedAt: now,
              lastSavedAt: now,
              isDirty: false
            };

            set((state) => ({
              works: state.works.map(w =>
                w.id === workId
                  ? { ...w, fichamento, updatedAt: now } as Work
                  : w
              )
            }));
            _lastMutationAt = Date.now();

            worksCache.set(getLibraryCacheKey(), get().works);
            saveReferencesDebounced(() => get().works, previousWorks);
            const parentWork = get().works.find(w => w.id === workId);
            auditLog({ operation: 'create', target: 'fichamento', targetId: fichamento.id, detail: `Fichamento criado para: ${parentWork?.title ?? workId}`, success: true });

            return fichamento;
          } catch (error: unknown) {
            set({ works: previousWorks });
            logger.error('[libraryStore.createFichamento]', String(error));
            bigwaToast.notifyError('Não foi possível criar o fichamento. Tente novamente.');
            auditLog({ operation: 'create', target: 'fichamento', targetId: workId, detail: `Falha ao criar fichamento para: ${workId}`, success: false, error: String(error) });
            throw error;
          }
        },

        // Update fichamento
        updateFichamento: (workId, updates) => {
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();

            set((state) => ({
              works: state.works.map(w => {
                if (w.id === workId && w.fichamento) {
                  const newContent = updates.content !== undefined ? updates.content : w.fichamento.content;
                  const wordCount = newContent.trim().split(/\s+/).filter(word => word.length > 0).length;
                  return {
                    ...w,
                    fichamento: {
                      ...w.fichamento,
                      ...updates,
                      wordCount,
                      updatedAt: now,
                      lastSavedAt: now,
                      isDirty: false
                    },
                    updatedAt: now
                  } as Work;
                }
                return w;
              })
            }));
            _lastMutationAt = Date.now();

            worksCache.set(getLibraryCacheKey(), get().works);
            saveReferencesDebounced(() => get().works, previousWorks);
            const parentWork = get().works.find(w => w.id === workId);
            const fichamentoId = parentWork?.fichamento?.id ?? workId;
            auditLog({ operation: 'update', target: 'fichamento', targetId: fichamentoId, detail: `Fichamento atualizado para: ${parentWork?.title ?? workId}`, success: true });
          } catch (error: unknown) {
            set({ works: previousWorks });
            logger.error('[libraryStore.updateFichamento]', String(error));
            bigwaToast.notifyError('Não foi possível salvar o fichamento. Alterações desfeitas.');
            const parentWork = previousWorks.find(w => w.id === workId);
            auditLog({ operation: 'update', target: 'fichamento', targetId: workId, detail: `Falha ao atualizar fichamento para: ${parentWork?.title ?? workId}`, success: false, error: String(error) });
          }
        },
        
        // Toggle fichamento status (draft <-> completed)
        toggleFichamentoStatus: (workId) => {
          const previousWorks = get().works;
          try {
            const now = new Date().toISOString();

            set((state) => ({
              works: state.works.map(w => {
                if (w.id === workId && w.fichamento) {
                  const newStatus = w.fichamento.status === 'draft' ? 'completed' : 'draft';
                  return {
                    ...w,
                    fichamento: {
                      ...w.fichamento,
                      status: newStatus,
                      updatedAt: now
                    },
                    updatedAt: now
                  } as Work;
                }
                return w;
              })
            }));
            _lastMutationAt = Date.now();

            // Save to Drive + cache asynchronously
            worksCache.set(getLibraryCacheKey(), get().works);
            saveReferencesDebounced(() => get().works, previousWorks);
            const parentWork = get().works.find(w => w.id === workId);
            const fichamentoId = parentWork?.fichamento?.id ?? workId;
            auditLog({ operation: 'update', target: 'fichamento', targetId: fichamentoId, detail: `Status alterado para: ${parentWork?.title ?? workId}`, success: true });
          } catch (error: unknown) {
            set({ works: previousWorks });
            logger.error('[libraryStore.toggleFichamentoStatus]', String(error));
            bigwaToast.notifyError('Não foi possível alterar o status do fichamento. Alteração desfeita.');
            const parentWork = previousWorks.find(w => w.id === workId);
            auditLog({ operation: 'update', target: 'fichamento', targetId: workId, detail: `Falha ao alterar status do fichamento para: ${parentWork?.title ?? workId}`, success: false, error: String(error) });
          }
        },
        
        // ═══ SCAN DE PDFs ÓRFÃOS ═══
      }),
    { name: 'LibraryStore' }
  )
);

// Helper function to check if work has incomplete required fields
export const isWorkIncomplete = (work: Work): boolean => {
  // Campos obrigatórios básicos
  if (!work.title?.trim() || !work.authors?.length || !work.year) {
    return true;
  }

  // Campos específicos por tipo
  switch (work.type) {
    case 'book':
      return !(work.publisher && typeof work.publisher === 'string' && work.publisher.trim()) || 
             !(work.city && typeof work.city === 'string' && work.city.trim()) || 
             !(work.isbn && typeof work.isbn === 'string' && work.isbn.trim());
    case 'article':
      return !(work.journal && typeof work.journal === 'string' && work.journal.trim()) || 
             !(work.volume && typeof work.volume === 'string' && work.volume.trim()) || 
             !(work.pages && typeof work.pages === 'string' && work.pages.trim());
    case 'chapter':
      return !(work.bookTitle && typeof work.bookTitle === 'string' && work.bookTitle.trim()) || 
             !(work.pages && typeof work.pages === 'string' && work.pages.trim());
    case 'thesis':
      return !(work.university && typeof work.university === 'string' && work.university.trim()) || 
             !(work.department && typeof work.department === 'string' && work.department.trim());
    case 'conference':
      return !(work.event && typeof work.event === 'string' && work.event.trim()) || 
             !(work.location && typeof work.location === 'string' && work.location.trim());
    case 'report':
      return !(work.institution && typeof work.institution === 'string' && work.institution.trim()) || 
             !(work.reportType && typeof work.reportType === 'string' && work.reportType.trim());
    case 'website':
      return !(work.url && typeof work.url === 'string' && work.url.trim());
    default:
      return false;
  }
};
